import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DiscussionNode } from '@proref/types';
import {
  useDeleteComment,
  useDiscussionsQuery,
  useEditComment,
  usePostComment,
  useVoteComment,
} from '../../hooks/use-discussions';
import { notifyError, notifySuccess } from '../../lib/toast';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { Icon } from '../shared/Icons';

// The clip's discussion, unlocked once the referee has answered. Fetch-based — live updates
// arrive with M8 (Realtime). Renders 2 levels: top-level comments each with their replies.
interface DiscussionThreadProps {
  clipId: string;
  // false until the viewer has answered the clip; the thread stays locked (and unfetched).
  unlocked: boolean;
}

export function DiscussionThread({ clipId, unlocked }: DiscussionThreadProps) {
  const { t } = useTranslation();
  const query = useDiscussionsQuery(clipId, unlocked);
  const post = usePostComment(clipId);
  const [draft, setDraft] = useState('');

  if (!unlocked) {
    return (
      <div className="mt-5 flex items-center gap-2 rounded border border-dashed border-line-2 bg-surface-2 px-3.5 py-3 text-sm text-muted">
        <Icon.lock className="h-4 w-4" />
        {t('discussion.unlockHint')}
      </div>
    );
  }

  async function submit() {
    const body = draft.trim();
    if (!body) return;
    try {
      await post.mutateAsync({ body });
      setDraft('');
      notifySuccess(t('discussion.postedToast'));
    } catch (err) {
      notifyError(err, t('discussion.postErrorToast'));
    }
  }

  const roots = query.data ?? [];

  return (
    <div className="mt-5 rounded border border-line bg-surface p-4 shadow-1">
      <div className="flex items-center gap-2 text-sm font-medium text-ink">
        <Icon.users className="h-4 w-4 text-accent" />
        {t('discussion.title')}
        {roots.length > 0 && <span className="text-xs font-normal text-muted">· {roots.length}</span>}
      </div>

      {/* New top-level comment. */}
      <div className="mt-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          placeholder={t('discussion.placeholder')}
          className="w-full resize-y rounded border border-line-2 bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none"
        />
        <div className="mt-2 flex justify-end">
          <Button variant="accent" size="sm" onClick={submit} disabled={!draft.trim() || post.isPending}>
            {post.isPending ? t('discussion.posting') : t('discussion.postComment')}
          </Button>
        </div>
      </div>

      {/* Thread. */}
      <div className="mt-4 space-y-4">
        {query.isLoading && (
          <div className="grid place-items-center py-6">
            <Spinner className="h-5 w-5 text-muted" />
          </div>
        )}
        {query.isError && (
          <div className="rounded border border-danger-tint bg-danger-tint px-3 py-2 text-xs text-danger">
            {t('discussion.couldNotLoad')}
          </div>
        )}
        {!query.isLoading && !query.isError && roots.length === 0 && (
          <p className="py-2 text-center text-xs text-muted">{t('discussion.noComments')}</p>
        )}
        {roots.map((node) => (
          <CommentItem key={node.id} node={node} clipId={clipId} depth={0} />
        ))}
      </div>
    </div>
  );
}

// How many replies to reveal per "show more" click (and on first open).
const REPLY_PAGE = 5;

// One comment + its replies (depth 1 only — replies can't be replied to, matching the API).
function CommentItem({ node, clipId, depth }: { node: DiscussionNode; clipId: string; depth: number }) {
  const { t } = useTranslation();
  const vote = useVoteComment(clipId);
  const edit = useEditComment(clipId);
  const del = useDeleteComment(clipId);
  const post = usePostComment(clipId);

  const [replying, setReplying] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(node.body ?? '');
  // Replies stay collapsed until asked for, then reveal REPLY_PAGE at a time — a comment with
  // 20 replies doesn't dump all 20 into the DOM at once. Closing resets the reveal to one page.
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [visibleReplies, setVisibleReplies] = useState(REPLY_PAGE);

  function toggleReplies() {
    if (repliesOpen) {
      setRepliesOpen(false);
      setVisibleReplies(REPLY_PAGE); // next open starts at 5 again
    } else {
      setRepliesOpen(true);
    }
  }

  async function toggleVote() {
    try {
      await vote.mutateAsync({ id: node.id, add: !node.has_voted });
    } catch (err) {
      notifyError(err, t('discussion.voteErrorToast'));
    }
  }

  async function submitReply() {
    const body = replyDraft.trim();
    if (!body) return;
    try {
      await post.mutateAsync({ body, parent_id: node.id });
      setReplyDraft('');
      setReplying(false);
      notifySuccess(t('discussion.replyPostedToast'));
    } catch (err) {
      notifyError(err, t('discussion.replyErrorToast'));
    }
  }

  async function submitEdit() {
    const body = editDraft.trim();
    if (!body) return;
    try {
      await edit.mutateAsync({ id: node.id, body });
      setEditing(false);
      notifySuccess(t('discussion.commentUpdatedToast'));
    } catch (err) {
      notifyError(err, t('discussion.editErrorToast'));
    }
  }

  async function remove() {
    try {
      await del.mutateAsync(node.id);
      notifySuccess(t('discussion.commentDeletedToast'));
    } catch (err) {
      notifyError(err, t('discussion.deleteErrorToast'));
    }
  }

  return (
    // Reply indentation + the connecting line are drawn by the parent's reply wrapper (below),
    // Facebook-style — so a reply itself needs no border here.
    <div className="min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="text-xs font-semibold text-ink">
            {node.is_deleted ? t('discussion.removed') : node.author?.full_name ?? t('discussion.unknown')}
          </span>
          {node.edited_at && !node.is_deleted && (
            <span className="ml-1.5 text-[10px] text-muted">{t('discussion.edited')}</span>
          )}
        </div>
        {/* Upvote — filled when the requester holds a vote. Hidden on a tombstone. */}
        {!node.is_deleted && (
          <button
            type="button"
            onClick={toggleVote}
            disabled={vote.isPending}
            aria-pressed={node.has_voted}
            className={[
              'flex flex-none items-center gap-1 rounded-btn border px-2 py-0.5 text-xs transition-colors',
              node.has_voted
                ? 'border-accent bg-accent-tint text-ink'
                : 'border-line-2 text-ink-2 hover:bg-surface-2',
            ].join(' ')}
          >
            <span aria-hidden>▲</span>
            {node.upvotes}
          </button>
        )}
      </div>

      {/* Body (or a tombstone) / inline edit box. */}
      {editing ? (
        <div className="mt-1.5">
          <textarea
            value={editDraft}
            onChange={(e) => setEditDraft(e.target.value)}
            rows={2}
            className="w-full resize-y rounded border border-line-2 bg-surface px-2.5 py-1.5 text-sm text-ink focus:border-accent focus:outline-none"
          />
          <div className="mt-1.5 flex gap-2">
            <Button variant="accent" size="sm" onClick={submitEdit} disabled={edit.isPending}>{t('discussion.save')}</Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>{t('discussion.cancel')}</Button>
          </div>
        </div>
      ) : (
        <p className={['mt-1 text-sm', node.is_deleted ? 'italic text-muted' : 'text-ink-2'].join(' ')}>
          {node.is_deleted ? t('discussion.commentRemovedBody') : node.body}
        </p>
      )}

      {/* Actions. Reply only on top-level comments (keeps the thread 2 levels). */}
      {!node.is_deleted && !editing && (
        <div className="mt-1.5 flex items-center gap-3 text-[11px] text-muted">
          {depth === 0 && (
            <button type="button" className="hover:text-ink" onClick={() => setReplying((v) => !v)}>
              {t('discussion.reply')}
            </button>
          )}
          {node.can_edit && (
            <button type="button" className="hover:text-ink" onClick={() => { setEditDraft(node.body ?? ''); setEditing(true); }}>
              {t('discussion.edit')}
            </button>
          )}
          {node.can_delete && (
            <button type="button" className="hover:text-danger" onClick={remove} disabled={del.isPending}>
              {t('discussion.delete')}
            </button>
          )}
        </div>
      )}

      {/* Reply composer. */}
      {replying && (
        <div className="mt-2">
          <textarea
            value={replyDraft}
            onChange={(e) => setReplyDraft(e.target.value)}
            rows={2}
            placeholder={t('discussion.replyPlaceholder')}
            className="w-full resize-y rounded border border-line-2 bg-surface px-2.5 py-1.5 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none"
          />
          <div className="mt-1.5 flex gap-2">
            <Button variant="accent" size="sm" onClick={submitReply} disabled={!replyDraft.trim() || post.isPending}>{t('discussion.reply')}</Button>
            <Button variant="ghost" size="sm" onClick={() => setReplying(false)}>{t('discussion.cancel')}</Button>
          </div>
        </div>
      )}

      {/* Replies — collapsed behind a toggle, revealed a page at a time. */}
      {node.replies.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={toggleReplies}
            className="flex items-center gap-1 text-[11px] font-medium text-accent hover:underline"
          >
            <span aria-hidden>{repliesOpen ? '▾' : '▸'}</span>
            {repliesOpen
              ? t('discussion.hideReplies')
              : t('discussion.showReplies', { count: node.replies.length })}
          </button>

          {repliesOpen && (
            (() => {
              const shown = node.replies.slice(0, visibleReplies);
              const hasMore = visibleReplies < node.replies.length;
              return (
                <div className="mt-3 space-y-3">
                  {shown.map((reply, i) => {
                    // Continue the vertical spine past this reply if another reply — or the
                    // "show more" button — follows it, so the line reads as one connected thread.
                    const continues = i < shown.length - 1 || hasMore;
                    return (
                      <div key={reply.id} className="relative pl-5">
                        {/* Elbow: a short vertical drop curving right into the reply (└). Its
                            horizontal meets the reply's NAME line (~10px down), not below it. */}
                        <span
                          aria-hidden
                          className="absolute left-1.5 top-0 h-2.5 w-3 rounded-bl-[6px] border-b border-l border-line-2"
                        />
                        {/* Spine to the next item, continuing straight down from the name line
                            and bridging the space-y gap. */}
                        {continues && (
                          <span aria-hidden className="absolute left-1.5 top-2.5 -bottom-3 w-px bg-line-2" />
                        )}
                        <CommentItem node={reply} clipId={clipId} depth={depth + 1} />
                      </div>
                    );
                  })}
                  {hasMore && (
                    <button
                      type="button"
                      onClick={() => setVisibleReplies((v) => v + REPLY_PAGE)}
                      className="relative pl-5 text-[11px] font-medium text-accent hover:underline"
                    >
                      {/* REPLY_PAGE (5) is always >1, so the displayed count and the count driving
                          plural selection land in the same CLDR category either way — safe to reuse
                          the one number for both, unlike a case where the display could show "5"
                          while a smaller true remainder picked the singular form. */}
                      {t('discussion.showMoreReplies', {
                        count: Math.min(REPLY_PAGE, node.replies.length - visibleReplies),
                      })}
                    </button>
                  )}
                </div>
              );
            })()
          )}
        </div>
      )}
    </div>
  );
}
